import { useMemo } from "react";

import { PrimaryButton } from "@fluentui/react/lib/Button";
import { Stack } from "@fluentui/react/lib/Stack";
import { Text } from "@fluentui/react/lib/Text";
import { TooltipHost, TooltipOverflowMode } from "@fluentui/react/lib/Tooltip";

import { WithTranslator } from "Components/Generic/Translator";
import { hasNoElements } from "Utils/Array";
import { WithTheme } from "../Theme/WithTheme";
import { IMergeProps } from "./IMergeProps";
import { MergeEntry } from "./MergeEntry";
import { useMergeStyles } from "./UseMergeStyles";

export const Merge = WithTheme(
  WithTranslator((props: IMergeProps): JSX.Element => {
    const {
      alternativeSourceToken,
      alternativeTargetToken,
      entries,
      onChange,
      onSelectAll,
      styleSet,
      theme: { spacing },
      translator
    } = props;

    const styles = useMergeStyles(styleSet);

    const mergeEntries = useMemo(
      () => entries.map((e, i) => <MergeEntry key={e.fieldName} index={i} onChange={onChange} {...e} />),
      [entries, onChange]
    );

    if (hasNoElements(mergeEntries)) {
      return null;
    }

    return (
      <Stack className={styles.root} tokens={{ childrenGap: spacing.s2 }}>
        <Stack horizontal verticalAlign="center" className={styles.header}>
          <Stack.Item disableShrink className={styles.label}>
            &nbsp;
          </Stack.Item>
          <Stack horizontal grow>
            <Stack.Item grow className={styles.origin}>
              <TooltipHost
                overflowMode={TooltipOverflowMode.Parent}
                content={translator.get(alternativeSourceToken, () => translator.get(`merge.source`, `Source`))}
              >
                <Text variant="mediumPlus">
                  {translator.get(alternativeSourceToken, () => translator.get(`merge.source`, `Source`))}
                </Text>
              </TooltipHost>
            </Stack.Item>
            <Stack.Item grow className={styles.target}>
              <TooltipHost
                overflowMode={TooltipOverflowMode.Parent}
                content={translator.get(alternativeTargetToken, () => translator.get(`merge.target`, `Target`))}
              >
                <Text variant="mediumPlus">
                  {translator.get(alternativeTargetToken, () => translator.get(`merge.target`, `Target`))}
                </Text>
              </TooltipHost>
            </Stack.Item>
          </Stack>
        </Stack>
        {mergeEntries}
        {onSelectAll != null && (
          <Stack horizontal verticalAlign="center" className={styles.footer}>
            <Stack.Item className={styles.label}>&nbsp;</Stack.Item>
            <Stack.Item className={styles.origin}>
              <PrimaryButton onClick={() => onSelectAll(`source`)}>Select all</PrimaryButton>
            </Stack.Item>
            <Stack.Item className={styles.target}>
              <PrimaryButton onClick={() => onSelectAll(`target`)}>Select all</PrimaryButton>
            </Stack.Item>
          </Stack>
        )}
      </Stack>
    );
  })
);
